import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon, RailBrand, RailFoot, SliderzMark } from '@orgazone/ui-web';
import { PanneauAide } from './screens/aide';
import { RAIL_BRAND, RAIL_NAV } from '@orgazone/ui-tokens';
import { ACCOUNT_PATH, NAV, RESTRICTED, groupOf } from './nav';
import { pastilleEcartsVisible, useCompteEcarts } from './compte-ecarts';
import { t } from './i18n';

/**
 * LE RAIL DU BACK-OFFICE — accordéon à un seul groupe ouvert.
 *
 * ⚠️ UN SEUL GROUPE OUVERT À LA FOIS, et AUCUN au démarrage (CADRAGE §4). Ce n'est pas une préférence
 * visuelle : à six groupes déroulés, le rail dépasse la hauteur de l'écran et se met à défiler — on
 * perd alors le pied de rail et l'accès restreint, c'est-à-dire ce qu'on cherche le moins souvent mais
 * qu'on doit toujours pouvoir atteindre. Au démarrage on arrive sur le tableau de bord, menu au repos.
 *
 * ⚠️ Exception à « aucun au démarrage » : arriver DIRECTEMENT sur une page profonde (lien partagé,
 * rechargement) ouvre son groupe. Un rail replié sur une page ouverte ne dit pas où l'on est.
 */
export function Rail({ user, onLogout }: {
  user: { name: string; role: string; permissions: string[] };
  /** Déconnexion RÉELLE, fournie par la coquille (elle seule sait s'il y a une session IdP à terminer). */
  onLogout: () => void | Promise<void>;
}) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // L'ouverture initiale suit la page servie, pas un défaut arbitraire (cf. commentaire ci-dessus).
  const [open, setOpen] = useState<string | null>(() => groupOf(pathname));
  const [aide, setAide] = useState(false);

  const restricted = RESTRICTED.filter((r) => !r.permission || user.permissions.includes(r.permission));
  // Le compte d'écarts OUVERTS — `null` tant qu'on ne sait pas : pas de pastille plutôt qu'un zéro
  // qui affirmerait « rien à traiter » (§23.0). Voir `compte-ecarts.ts`.
  const ecartsOuverts = useCompteEcarts();

  return (
    <>
      {/* ⚠️ AGENCEMENT `product` — la maquette met « Sliderz » AU-DESSUS et « BACKOFFICE » dessous en
          mono capitales, l'inverse des trois autres SPA. Métriques du relevé : monogramme 28×29. */}
      <RailBrand layout="product" logo={<SliderzMark size={RAIL_BRAND.subtitle.logoWidth} height={RAIL_BRAND.subtitle.logoHeight} />} surtitle={t('Backoffice')} name="Sliderz" />

      {/* Écartement, retrait et tailles d'icône : relevés sur la maquette (`nav` gap 6 ; sous-liste
          gap 1, retrait 30, respiration basse 8). */}
      {/* ⚠️ LE FILET EST ICI, pas sous la marque : c'est le relevé, et c'est ce qui le met EN RETRAIT
          du rembourrage de l'aside plutôt qu'à cheval sur toute la largeur. Le rembourrage horizontal
          vient de l'aside (`RAIL_PADDING` de la charte, désormais le défaut de la coquille et donc le
          même pour les quatre surfaces), donc ce nav n'en pose aucun.
          ⚠️ Ses trois mesures viennent de la charte (`navEdge`) depuis que l'EDI porte le même
          agencement : recopiées, elles auraient divergé au premier ajustement. */}
      <nav style={{
        display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflowY: 'auto',
        ...RAIL_BRAND.subtitle.navEdge,
      }}>
        {NAV.map((entry) => {
          if (entry.kind === 'leaf') {
            // La pastille ne paraît QUE s'il y a réellement quelque chose à traiter : ni pendant le
            // chargement, ni après un échec de lecture, ni à zéro (la maquette la conditionne aussi).
            const compte = entry.path === '/ecarts' ? ecartsOuverts : null;
            const avecPastille = pastilleEcartsVisible(compte);
            return (
              <NavLink key={entry.path} to={entry.path} end className={({ isActive }) => 'nav' + (isActive ? ' active' : '')}>
                <Icon name={entry.icon} size={RAIL_NAV.iconSize} />
                <span style={avecPastille ? { flex: 1 } : undefined}>{t(entry.label)}</span>
                {avecPastille && (
                  <span
                    title={t('{n} écart à traiter', { n: compte })}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 18, height: 18, padding: '0 5px', borderRadius: 'var(--r-pill)',
                      background: 'var(--danger)', color: '#fff', fontSize: 'var(--t-sub)', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >{compte}</span>
                )}
              </NavLink>
            );
          }
          const isOpen = open === entry.id;
          const holdsCurrent = groupOf(pathname) === entry.id;
          return (
            <div key={entry.id}>
              {/* L'en-tête de groupe n'est PAS un lien : un groupe n'a pas de page à lui. Le marquer
                  « actif » quand il contient la page courante dit où l'on est même replié. */}
              <button
                type="button"
                className={'nav' + (holdsCurrent && !isOpen ? ' active' : '')}
                style={{ width: '100%' }}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : entry.id)}
              >
                <Icon name={entry.icon} size={RAIL_NAV.iconSize} />
                <span style={{ flex: 1, textAlign: 'left' }}>{t(entry.label)}</span>
                <Icon name={isOpen ? 'chevronU' : 'chevronD'} size={14} />
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '2px 0 8px 30px' }}>
                  {entry.children.map((c) => (
                    <NavLink key={c.path} to={c.path} className={({ isActive }) => 'nav sub' + (isActive ? ' active' : '')}>
                      {t(c.label)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* ACCÈS RESTREINT — rendu SEULEMENT si le droit est détenu. Le bloc entier disparaît avec son
            surtitre : un intitulé « Accès restreint » suivi de rien serait une porte qu'on montre sans
            pouvoir l'ouvrir. */}
        {restricted.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--sidebar-line)' }}>
            <div style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--sidebar-text-dim)', padding: '0 12px 6px' }}>
              
              {t('Accès restreint')}
            </div>
            {restricted.map((r) => (
              <NavLink key={r.path} to={r.path} className={({ isActive }) => 'nav' + (isActive ? ' active' : '')}>
                <Icon name="pulse" size={RAIL_NAV.iconSize} />
                <span>{t(r.label)}</span>
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      {/* ⚠️ L'AIDE VIT AU BAS DU RAIL, pas dans une rubrique où l'on se rend (relevé de la maquette).
          Elle s'ouvre PAR-DESSUS l'écran courant et commence par en parler : une page séparée
          obligerait à quitter ce qu'on fait, chercher le bon article, puis revenir — trois gestes
          pendant lesquels on a oublié la question.
          ⚠️ MÉTRIQUES ET LIBELLÉ RELEVÉS : « Aide & tutoriels » (et non « Aide »), icône point
          d'interrogation 19 px trait 1,75, hauteur 40, écart 11, texte 13,5 px en graisse 500, et la
          couleur PLEINE du rail — pas la version atténuée, qui la ferait passer pour désactivée. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 8 }}>
        <button type="button" onClick={() => setAide(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 11, height: 40, padding: '0 12px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--sidebar-text)', fontSize: '13.5px', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}>
          <Icon name="help" size={19} stroke={1.75} />
          <span style={{ flex: 1 }}>{t('Aide & tutoriels')}</span>
        </button>
      </div>
      {aide ? <PanneauAide pathname={pathname} search={search} onClose={() => setAide(false)} /> : null}

      {/* Le pied de rail dit QUI est connecté et ferme la session — le nom et le rôle viennent de `/v1/auth/me`,
          et le bouton révoque vraiment (voir `App.tsx`, qui termine aussi la session de l'IdP quand il y en a une).
          ⚠️ LE NOM OUVRE « MON COMPTE » (maquette) : ce qu'on y règle ne vaut que pour soi — langue, mot de passe,
          sessions —, là où le groupe « Réglages » règle le MAGASIN. L'écran n'a donc pas d'entrée dans le menu. */}
      <RailFoot name={user.name} role={user.role} onLogout={() => { void onLogout(); }}
        account={{ onOpen: () => navigate(ACCOUNT_PATH), active: pathname === ACCOUNT_PATH, title: t('Mon compte — langue, mot de passe, sessions') }} />
    </>
  );
}

/** ⚠️ `NAV_MIN_HEIGHT` RETIRÉ (2026-09-05) : exporté, jamais consommé — et devenu faux, la hauteur
 *  d'un item étant désormais VERROUILLÉE (`RAIL_NAV.height`), pas minimale. */
